import React from 'react';
import { useTranslation } from 'react-i18next';
import ScopedCssBaseline from '@mui/material/ScopedCssBaseline';
import { makeStyles } from '@mui/styles';
import { ThemeProvider } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';
import useMediaUrl from 'hooks/useMediaURL';
import PlayIcon from '@mui/icons-material/PlayCircleFilledWhiteOutlined';
import SingleStepForm from './SingleStepForm';
import MultiStepForm from './MultiStepForm';
import DesignContainer from './DesignContainer';
import getTheme from 'modules/connect-card-widget/utils/getTheme';

const useStyles = makeStyles((theme) => {
  return {
    logoBox: {
      'margin': 'auto',
      'width': '140px',
      'height': '80px',
      'maxHeight': '80px',
      'display': 'flex',
      'alignItems': 'center',
      'justifyContent': 'center',
      'marginBottom': '24px',
      '&.placeholder': {
        border: '1px dashed #2186eb',
        color: '#2186eb',
      },
      '& img': {
        width: '100%',
        maxWidth: '100%',
        maxHeight: '100%',
        height: 'auto',
        objectFit: 'contain',
      },
    },
    video: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: '1px solid #e4e7eb',
      maxWidth: '275px',
      width: '100%',
      height: '160px',
      margin: 'auto',
      marginBottom: '20px',
      boxShadow: '0 0 2px 0 rgba(0, 0, 0, 0.2)',
    },
    footer: {
      textAlign: 'center',
    },
  };
});

const ConnectCardPreview = ({
  data,
  questions,
  selectedQuestion,
  onQuestionChange,
}) => {
  const {
    card_headline,
    card_description,
    logo_or_video_url,
    logo_or_video,
    card_background_color,
    card_text_color,
  } = data;
  const [t] = useTranslation('groups');
  const account = useSelector((s) => s.settings.account);
  const logoUrl = useMediaUrl(account.account_logo_url);

  const classes = useStyles();

  return (
    <ThemeProvider
      theme={getTheme({
        primary: card_background_color,
        text: card_text_color,
      })}
    >
      <Box
        borderRadius={3}
        overflow="hidden"
        display="flex"
        flexDirection="column"
      >
        <ScopedCssBaseline>
          <DesignContainer color={card_background_color}>
            <Box textAlign="center" mb={3}>
              {logo_or_video === 'video' ? (
                <div className={classes.video}>
                  <PlayIcon />
                </div>
              ) : logo_or_video_url || logoUrl?.url ? (
                <div className={classes.logoBox}>
                  <img
                    className={classes.logo}
                    src={logo_or_video_url || logoUrl.url}
                  />
                </div>
              ) : (
                <div className={classes.video}>{t('cc-logo', 'Your Logo')}</div>
              )}
              <Typography
                gutterBottom
                variant="h2"
                color={card_background_color}
                fontWeight={500}
              >
                {card_headline || 'Headline'}
              </Typography>
              <Typography paragraph>
                {card_description || 'Description'}
              </Typography>
            </Box>
            <Box>
              {data.card_type === 'standard' && (
                <SingleStepForm
                  onQuestionChange={onQuestionChange}
                  questions={questions}
                  selectedQuestion={selectedQuestion}
                />
              )}
              {data.card_type === 'multistep' && (
                <MultiStepForm
                  selectedQuestion={selectedQuestion}
                  onQuestionChange={onQuestionChange}
                  questions={questions}
                />
              )}
            </Box>
            <Box className={classes.footer} mt={4} pb={2}>
              <Typography
                paragraph
                variant="small"
                component="p"
                color="text.secondary"
              >
                By submitting, you agree to receive communications from us. Text
                STOP to cancel or HELP for assistance
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Powered by{' '}
                <Typography
                  variant="caption"
                  color="textPrimary"
                  fontWeight={600}
                >
                  Text In Church
                </Typography>
              </Typography>
            </Box>
          </DesignContainer>
        </ScopedCssBaseline>
      </Box>
    </ThemeProvider>
  );
};

export default ConnectCardPreview;
